import React, { useEffect, useState } from "react";
import { RegisterProvider, useRegisterContext } from "src/contexts/RegisterContext";
import { EmailVerification } from "src/components/Register";
import { Password } from "src/components/Register/Password/Password";
import { DateOfBirth } from "src/components/Register/DateOfBirth/DateOfBirth";
import { Address } from "src/components/Register/Address/Address";
import { Accessibility } from "src/components/Register/Accessibility/Accessibility";
import { Gender } from "src/components/Register/Gender/Gender";
import { PhoneNumber } from "src/components/Register/PhoneNumber/PhoneNumber";
import { PhoneNumberVerification } from "src/components/Register/PhoneNumberVerification/PhoneNumberVerification";
import { SessionInterface, SessionDetailsInterface } from "src/models/Session";
import { generateSessionDetails, SessionDetails, MeshGradient, Stepper, NavigationBar } from "src/components";
import {
  RegistrationFlowContainer,
  RegistrationStepContainer,
  SessionPanelContainer,
  SessionPanelContent,
} from "./BookingRegistrationFlow.styles";

interface BookingRegistrationFlowProps {
  email: string;
  firstName: string;
  selectedSession: SessionInterface;
  showName?: string | null;
  showMinimumAge?: number | null;
  onRegistrationComplete: () => void;
  onBackToSessionSelection: () => void;
}

const TOTAL_STEPS = 8;

const BookingRegistrationFlowInner: React.FC<BookingRegistrationFlowProps> = ({
  email,
  firstName,
  selectedSession,
  showName,
  showMinimumAge,
  onRegistrationComplete,
  onBackToSessionSelection,
}) => {
  const { setRegisterInfo } = useRegisterContext();
  const [step, setStep] = useState(0);
  const [sessionDetails, setSessionDetails] = useState<SessionDetailsInterface>({
    name: "",
    date: "",
    arrivalTime: "",
    approxFinishTime: "",
    location: null,
  });

  useEffect(() => {
    setRegisterInfo((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        email: email ? email : "",
      },
    }));
  }, []);

  useEffect(() => {
    if (selectedSession) {
      setSessionDetails(generateSessionDetails(selectedSession));
    }
  }, [selectedSession]);

  const nextStep = () => {
    if (step === TOTAL_STEPS - 1) {
      onRegistrationComplete();
    } else {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step === 0) {
      onBackToSessionSelection();
      return;
    }
    setStep(step - 1);
  };

  return (
    <>
      <NavigationBar showHomeIconOnMobile showMenu={false}>
        <Stepper stepSize={TOTAL_STEPS} activeStepIndex={step} />
      </NavigationBar>
      <RegistrationFlowContainer>
        <RegistrationStepContainer>
          {step === 0 && <EmailVerification onNextButtonClick={nextStep} onPrevButtonClick={prevStep} />}
          {step === 1 && <Password onNextButtonClick={nextStep} />}
          {step === 2 && <DateOfBirth onNextButtonClick={nextStep} onPrevButtonClick={prevStep} />}
          {step === 3 && <Address onNextButtonClick={nextStep} onPrevButtonClick={prevStep} />}
          {step === 4 && <Accessibility onNextButtonClick={nextStep} onPrevButtonClick={prevStep} />}
          {step === 5 && <Gender onNextButtonClick={nextStep} onPrevButtonClick={prevStep} />}
          {step === 6 && <PhoneNumber onNextButtonClick={nextStep} onPrevButtonClick={prevStep} />}
          {step === 7 && <PhoneNumberVerification onNextButtonClick={nextStep} onPrevButtonClick={prevStep} />}
        </RegistrationStepContainer>

        <SessionPanelContainer>
          <SessionPanelContent>
            <SessionDetails
              showName={showName}
              showMinimumAge={showMinimumAge}
              sessionDetails={sessionDetails}
            />
          </SessionPanelContent>
          <MeshGradient
            colour="pink-purple-green"
            position="bottom"
            size="low"
            sx={{ top: 0 }}
          />
        </SessionPanelContainer>
      </RegistrationFlowContainer>
    </>
  );
};

const BookingRegistrationFlow: React.FC<BookingRegistrationFlowProps> = (props) => {
  return (
    <RegisterProvider>
      <BookingRegistrationFlowInner {...props} />
    </RegisterProvider>
  );
};

export default BookingRegistrationFlow;
